import { Head, Link, router } from "@inertiajs/react"
import { Check, Inbox, MessageSquare, X } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import VerifiedBadge from "@/components/verified-badge"
import AppLayout from "@/layouts/app-layout"
import { t } from "@/lib/i18n"
import { formatRelativeTime } from "@/lib/time"
import { typedPatch } from "@/lib/typed-router"
import type { AcceptConversation } from "@/types/router-mutations"
import type { ConversationsIndex } from "@/types/serializers"

function ConversationList({
  conversations,
}: {
  conversations: ConversationsIndex["conversations"]
}) {
  return (
    <div className="divide-border border-border bg-card divide-y rounded-lg border">
      {conversations.map((conv) => {
        const participant = conv.other_participant
        const roleLabel =
          t.roles[participant.role as keyof typeof t.roles] ?? participant.role
        const hasUnread = conv.unread_count > 0

        return (
          <Link
            key={conv.id}
            href={`/mensajes/${conv.id}`}
            className="hover:bg-accent flex items-center gap-3 px-4 py-3 transition-colors"
          >
            <div className="relative shrink-0">
              <Avatar className="h-12 w-12">
                <AvatarImage
                  src={participant.avatar_url ?? undefined}
                  alt={participant.full_name}
                />
                <AvatarFallback className="bg-krown-200 text-sm font-bold text-white">
                  {participant.first_name[0]}
                  {participant.last_name[0]}
                </AvatarFallback>
              </Avatar>
              {hasUnread && (
                <span className="bg-krown-100 border-card absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm ${hasUnread ? "font-bold" : "font-medium"}`}
                >
                  {participant.full_name}
                </span>
                <Badge variant="outline" className="text-xs">
                  {roleLabel}
                </Badge>
                <VerifiedBadge show={participant.profile_verified} size="sm" />
              </div>
              {conv.last_message && (
                <p
                  className={`truncate text-sm ${hasUnread ? "text-foreground font-medium" : "text-muted-foreground"}`}
                >
                  {conv.last_message.body}
                </p>
              )}
            </div>

            <div className="flex shrink-0 flex-col items-end gap-1">
              {conv.last_message && (
                <span className="text-muted-foreground text-xs">
                  {formatRelativeTime(conv.last_message.created_at)}
                </span>
              )}
              {hasUnread && (
                <span className="bg-krown-200 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold text-white">
                  {conv.unread_count}
                </span>
              )}
            </div>
          </Link>
        )
      })}
    </div>
  )
}

function RequestList({
  requests,
}: {
  requests: ConversationsIndex["requests"]
}) {
  function handleAccept(id: number, e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    typedPatch<AcceptConversation>(`/mensajes/${id}`, {
      conversation: { accepted: true },
    })
  }

  function handleReject(id: number, e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    router.delete(`/mensajes/${id}`)
  }

  return (
    <div className="divide-border border-border bg-card divide-y rounded-lg border">
      {requests.map((conv) => {
        const participant = conv.other_participant
        const roleLabel =
          t.roles[participant.role as keyof typeof t.roles] ?? participant.role

        return (
          <div key={conv.id} className="flex items-center gap-3 px-4 py-3">
            <div className="shrink-0">
              <Avatar className="h-12 w-12">
                <AvatarImage
                  src={participant.avatar_url ?? undefined}
                  alt={participant.full_name}
                />
                <AvatarFallback className="bg-krown-200 text-sm font-bold text-white">
                  {participant.first_name[0]}
                  {participant.last_name[0]}
                </AvatarFallback>
              </Avatar>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {participant.full_name}
                </span>
                <Badge variant="outline" className="text-xs">
                  {roleLabel}
                </Badge>
                <VerifiedBadge show={participant.profile_verified} size="sm" />
              </div>
              {conv.last_message && (
                <p className="text-muted-foreground truncate text-sm">
                  {conv.last_message.body}
                </p>
              )}
            </div>

            <div className="flex shrink-0 gap-2">
              <Button
                size="sm"
                variant="default"
                onClick={(e) => handleAccept(conv.id, e)}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                Aceptar
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={(e) => handleReject(conv.id, e)}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                Rechazar
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function ConversationsIndex({
  conversations,
  requests,
}: ConversationsIndex) {
  return (
    <AppLayout>
      <Head title="Mensajes | KrownFT" />

      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-2xl px-4 py-6">
          <div className="mb-6 flex items-center gap-2">
            <MessageSquare className="text-krown-100 h-5 w-5" />
            <h1 className="text-2xl font-bold">Mensajes</h1>
          </div>

          <Tabs defaultValue="mensajes">
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="mensajes" className="flex-1">
                Mensajes
              </TabsTrigger>
              <TabsTrigger value="solicitudes" className="flex-1 gap-2">
                Solicitudes
                {requests.length > 0 && (
                  <span className="bg-krown-200 ml-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold text-white">
                    {requests.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="mensajes">
              {conversations.length > 0 ? (
                <ConversationList conversations={conversations} />
              ) : (
                <div className="py-16 text-center">
                  <MessageSquare className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                  <p className="text-muted-foreground text-lg">
                    {t.conversations.noConversations}
                  </p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t.conversations.noConversationsMessage}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="solicitudes">
              {requests.length > 0 ? (
                <RequestList requests={requests} />
              ) : (
                <div className="py-16 text-center">
                  <Inbox className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                  <p className="text-muted-foreground text-lg">
                    No tenés solicitudes de mensajes
                  </p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Cuando alguien que no seguís te envíe un mensaje, aparecerá
                    acá.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppLayout>
  )
}
